import * as THREE from "three";
import { airAimPoint } from "./enemyAim";
import { RigidRig } from "../rigidRig";
import { Enemy, LevelSim, SimConstants, SimEventKind } from "@highmarch/domain";
import { hqEnemyName, hqNode, lib } from "./common";
import { SkinKits } from "../skins/kit/runtime";
import { UnitRings } from "./blobShadow";
import { UnitFx } from "../unitFx";
import { reducedVfx } from "../../ui/settings";
import type { VFX } from "../../../../../art/vfx.js";
import { impactGate, roundOf, shotKey, statusGate } from "../statusFx";
import { BossEvents, BossHandle } from "./bossFx";
import { BossBeats, BossStage } from "./bossStage";
import { EnemyLook, enemyLook } from "./enemyLook";
import { hasOwnArt } from "./enemyStandIns";
import { EnemyMarks } from "./enemyMarks";
import { EMERGE_SECONDS, EnemyView, FLOAT, PLATE_LIFT, emergeScale } from "./enemyView";
import { drawMarks, floatAt, floatAtBase, hitFeel, pushRing, readStatus, showHp, stepUnit } from "./enemyFeel";
import { blastFrom, collectShots, dieStep, startDeath } from "./enemyDeath";
import { KillFx } from "./killFx";
import { buildEnemyView, enemyModel, resetEnemyView } from "./enemyBuild";
import { ghostOf, ghostTick } from "./enemyGhost";
import { rareIndexOf, rareOfChapter } from "../../rare/rareApi";
import { isWyrmArchetype, wyrmDef } from "../../rare/wyrmApi";
import { lodPick } from "./enemyBuild";
import { Showpiece, ShowpieceDrawn } from "./showpiece";
import { flyHeight, wyrmArrive, wyrmFlee, wyrmRoar } from "./wyrmView";
import { bossAbility, bossArrive } from "./bossClips";
import { EnemyStrike } from "./enemyStrike";
import { HealerCasts } from "./healerCast";

export type { BossHandle };

const _floatCol = new THREE.Color();

/**
 * Pooled enemies bound by id, interpolated between sim ticks. HQ units play `walk` while moving, `hit` on a heavy
 * hit and `die` on death; the plain capsules (no library) hop along the road.
 *
 * The "make it pop" layer (art review units F1–F6) on top: a per-view shader look (hit flash, red team rim, status
 * tints, dissolve — unitFx.ts), per-archetype scale, footfall squash, lean and knockback, a spawn pop, deaths that
 * dissolve instead of sinking through the ground (or fly on a blast).
 *
 * Each unit's type reads from its role (enemyLook.ts): a size and the shared marks layer's (no colour wash on the body),
 * badge, HP bar, status icons, ground ring and aura, plus the floating kill and leak numbers (enemyMarks.ts). The boss
 * keeps its rune ring and telegraphs (UnitRings, bossStage.ts) and shows its health on the DOM boss bar.
 */
export class EnemyViews {
  readonly group = new THREE.Group();
  private readonly active = new Map<number, EnemyView>();
  private readonly dying: EnemyView[] = [];
  private readonly pool = new Map<string, EnemyView[]>();
  /** Badge, HP bar, status icons, ground ring and aura of every enemy, and the floating numbers (Mac readability). */
  readonly marks = new EnemyMarks(SimConstants.MaxEnemies);
  /** The boss's rune ring and its telegraphs. */
  private readonly rings = new UnitRings();
  /** Model height per model key, measured once. */
  private readonly heights = new Map<string, number>();
  private time = 0;
  /** The reduced-effects setting, read once per sim tick (its media query allocates; not every frame). */
  private reduced = false;
  /** This tick's blast impacts (target x, z, tower x, z), rebuilt in `onTick`. */
  private readonly impacts: number[] = [];
  /** A held raider's blows timed to the sim's strikes (enemyStrike.ts). */
  private readonly strike = new EnemyStrike();
  /** When a healer casts for its heals (healerCast.ts). */
  private readonly casts = new HealerCasts();
  /**
   * Where the bodies that died since the last tick were drawn when they died (the router's death beats read it after
   * the view has let the body go: `feet`). Cleared each tick.
   */
  private readonly fell = new Map<number, THREE.Vector3>();
  /**
   * Each tower's latest round (its `shotKey`). A burn tick is dealt before its tower's TowerFired in the buffer, so it
   * is not a round's (roundOf); but the fireball that lit the burn may still be in the air, and a burn tick that kills
   * then must not drop the body before that fireball lands: the kill waits on the tower's latest round, which lands
   * with it, or at once (the router settles an unclaimed key) if it has already landed.
   */
  private readonly lastRound = new Map<number, number>();

  /** The sprite VFX, once loaded (the level sets it): death beats and boss dust. Null until then — beats are skipped. */
  fx: (Pick<VFX, "sparkles"> & { dust?(pos: THREE.Vector3, n: number, opts?: Record<string, unknown>): void; enemyStatus?: VFX["enemyStatus"] }) | null = null;
  /**
   * The boss moments' outside beats (camera trauma, hit-stop, DOM screen flash, coins to the HUD), set by the level;
   * each may stay null. The sprite dust comes from `fx`.
   */
  readonly beats: BossBeats = { dust: null, addTrauma: null, hitStop: null, screenFlash: null, coinsTo: null };
  /**
   * Boss event listener lists (PHASE2_BRIEF "Boss events"): push a function to subscribe. `onBossSpawn(boss)` as it
   * starts to rise, `onBossHp(frac, boss)` when its health moves, `onBossPhase(n, boss)` at the 50 % phase change,
   * `onBossDeath(boss, worldPos)` as it falls (worldPos = its feet).
   */
  readonly bossEvents = new BossEvents();
  readonly onBossSpawn = this.bossEvents.onBossSpawn;
  readonly onBossHp = this.bossEvents.onBossHp;
  readonly onBossPhase = this.bossEvents.onBossPhase;
  readonly onBossDeath = this.bossEvents.onBossDeath;
  readonly onBossGone = this.bossEvents.onBossGone;
  private readonly stage: BossStage;
  /** A unit's ground ring this frame (enemyFeel.ts pushRing). */
  private readonly ringOf = (v: EnemyView, hover: number, fade: number): void => pushRing(v, hover, fade, this.marks, this.rings, this.stage, this.time);
  /** M36/M40: the rare's or the Starfall Wyrm's shine, motes and trails (showpiece.ts). */
  private readonly showpiece = new Showpiece(this.group, () => this.fx, () => this.beats.screenFlash);
  /**
   * Where the rare (or the Starfall Wyrm, M40) is drawn this frame, its drawn height, whether it is alive, and the middle
   * of its body in the world (a flyer's is up in the air) — null when there is none (rareMoment.ts, wyrmMoment.ts).
   */
  rareDrawn(): ShowpieceDrawn | null { return this.showpiece.drawn((v) => v.dead <= 0 && this.active.has(v.bs?.handle.id ?? -1)); }

  /** M40: the living Wyrm roars (a health threshold); false when it is busy with another clip or not on screen. */
  wyrmRoar(enemyId: number): boolean {
    const v = this.active.get(enemyId);
    return !!v && v.wyrm && wyrmRoar(v);
  }

  /** M40: the living Wyrm's flight loop and where it is in it (its wingbeats, level/wyrmAudio.ts); null while a one-shot plays or it is gone. */
  wyrmWing(enemyId: number): { readonly clip: string; readonly phase: number } | null {
    const v = this.active.get(enemyId);
    if (!v || !v.wyrm || v.dead > 0 || v.rig.busy || v.rig.loopClip === "") return null;
    this.wing.clip = v.rig.loopClip; this.wing.phase = v.rig.loopPhase();
    return this.wing;
  }
  private readonly wing = { clip: "", phase: 0 };

  constructor(private readonly sim: LevelSim) {
    this.group.add(this.rings.mesh, this.marks.group);
    // the dust beat reads `fx` late: the atlas loads after the view exists
    this.beats.dust = (p, n, o) => this.fx?.dust?.(p, n, o);
    this.stage = new BossStage(sim, this.bossEvents, this.beats, this.rings);
  }

  /** World position of a node on an enemy's model (`weapon`, `bomb`), read after the rig update; null when absent. */
  nodeWorld(enemyId: number, suffix: string): THREE.Vector3 | null {
    const v = this.active.get(enemyId);
    if (!v) return null;
    if (!v.hq) return null;
    const e = this.sim.findEnemy(enemyId);
    if (!e) return null;
    const node = hqNode(v.root, hqEnemyName(this.sim.balance.enemies[e.archetype].id), suffix);
    return node ? node.getWorldPosition(new THREE.Vector3()) : null;
  }

  /**
   * Where a live enemy is drawn this frame, for picking it with a finger: the middle of its body and its badge plate
   * (world), a flyer at its flying height. Null when it has no view.
   */
  drawnPoints(enemyId: number): [THREE.Vector3, THREE.Vector3] | null {
    const v = this.active.get(enemyId);
    if (!v) return null;
    const p = v.root.position, feet = p.y + flyHeight(v), head = v.hq ? v.headNow : v.headY;
    return [new THREE.Vector3(p.x, feet + head / 2, p.z), new THREE.Vector3(p.x, feet + head + PLATE_LIFT, p.z)];
  }

  /**
   * Where an enemy is drawn this frame (its feet, x and z; y is the view's), alive or walking on doomed — or, if it died
   * since the last tick, where it fell. The rounds fly at this and the death beats burst here, so they meet the body the
   * player sees rather than the sim's spot for it. Null when it has no view.
   */
  feet(enemyId: number): THREE.Vector3 | null {
    return this.active.get(enemyId)?.root.position ?? this.fell.get(enemyId) ?? null;
  }
  /** Where weapons aim at a live flyer (the middle of its hovering body, enemyAim.ts); null for a ground unit. */
  airAim(enemyId: number): THREE.Vector3 | null { const v = this.active.get(enemyId); return v && !v.doomed ? airAimPoint(v, new THREE.Vector3()) : null; }

  /**
   * M36: a word over a live enemy's head (IMMUNE when an immunity shrugs a hit off), in `hex`; false when it has no view.
   * The marks layer spells digits, + − and the letters of IMMUNE (enemyGlyphs.ts LETTER_CELL).
   */
  floatOver(enemyId: number, text: string, hex: number, px: number = FLOAT.killPx): boolean {
    const v = this.active.get(enemyId);
    if (!v || v.doomed) return false;
    floatAt(this.marks, v, text, _floatCol.setHex(hex), px);
    return true;
  }

  /** The view of a live enemy (ENEMIES_V2's ability effects play its clips and sink a Burrower), or undefined. */
  viewOf(id: number): EnemyView | undefined { return this.active.get(id); }
  /** Tier IV (M21): hides a raider's body while Polymorph has it a sheep (render/views/tier4Creatures.ts draws the sheep). */
  setBodyHidden(enemyId: number, hidden: boolean, headY = -1): void {
    const v = this.active.get(enemyId);
    if (!v) return;
    v.body.visible = !hidden;
    v.headY = hidden && headY > 0 ? headY : v.headRest;       // the plate rides on the body drawn in its place (the wild pup)
  }

  /** The view of a live boss, for the Phase 2 sequences (null when `id` is not a boss on screen). */
  boss(id: number): BossHandle | null {
    const v = this.active.get(id);
    return v && v.bs ? v.bs.handle : null;
  }

  /**
   * Leaving the level: frees what the views own on the GPU — each clone's per-instance bone texture, each view's
   * patched material clones, the ring and bar instancing. Geometry and source materials are the asset library's.
   */
  dispose(): void {
    const all = [...this.active.values(), ...this.dying, ...[...this.pool.values()].flat()];
    for (const v of all) { RigidRig.dispose(v.body); v.look?.dispose(); }
    this.active.clear(); this.dying.length = 0; this.pool.clear(); this.fell.clear(); this.lastRound.clear(); this.strike.clear();
    this.rings.dispose(); this.marks.dispose();
    this.showpiece.dispose();
    this.group.clear();
  }

  onTick(): void {
    const sim = this.sim;
    this.fell.clear();
    for (const v of this.active.values()) {
      v.prev.copy(v.cur); v.runHp = v.simHp;
      if (v.doomed) ghostTick(v.ghost, sim.road.roadCells, v.cur);   // dead, its round still flying: it walks on
    }
    for (const e of sim.enemySlots) {
      if (!e.alive) continue;
      let v = this.active.get(e.id);
      if (!v) {
        v = this.acquire(e);
        this.active.set(e.id, v);
        v.prev.copy(v.cur.set(e.x.toNumber(), 0, e.y.toNumber()));
        v.root.position.copy(v.cur);                   // drawn there from the first ask (`feet`), not where the pooled view last stood
        if (v.bs) this.stage.spawn(v.bs, e.id, v.archId, this.reduced, !v.wyrm);
        if (v.wyrm) wyrmArrive(v);                     // M40: it dives in from the sky (no rise out of the ground)
        else if (v.bs) bossArrive(v);                  // bosses v2: it roars as it climbs out (bossClips.ts)
      } else v.cur.set(e.x.toNumber(), 0, e.y.toNumber());
      readStatus(e, v, !!this.fx, this.sim.balance);
    }
    collectShots(this.sim, this.impacts);
    const reduced = this.reduced = reducedVfx();
    const events = sim.events.all();
    // A tower's hits and kills show when its round lands (statusFx.ts ImpactGate). The rounds fly under reduced effects
    // too (effects.ts routes the shots then, without the extras); without the sprite VFX nothing flies, so those show
    // at once, as before.
    const rounds = roundOf(events), gated = this.fx !== null;
    for (let i = 0; i < events.length; i++) {
      const ev = events[i];
      const key = gated && rounds[i] > 0 ? shotKey(sim.tick, rounds[i]) : -1;
      if (ev.kind === SimEventKind.TowerFired) { if (gated) this.lastRound.set(ev.a, shotKey(sim.tick, ev.a)); }
      else if (ev.kind === SimEventKind.EnemyDamaged) { const v = this.active.get(ev.a); if (v && !v.doomed) this.onDamaged(ev.a, ev.b, ev.c, rounds[i] > 0, v, key, reduced); }
      else if (ev.kind === SimEventKind.EnemyKilled) {
        const v = this.active.get(ev.a);
        const burnKey = key < 0 && gated && ev.b > 0 ? this.lastRound.get(ev.b) ?? -1 : -1;   // a burn tick's kill
        if (v && !v.doomed) this.onKilled(ev.a, ev.c, v, key >= 0 ? key : burnKey, reduced);
      }
      else if (ev.kind === SimEventKind.EnemyLeaked) floatAtBase(this.marks, sim.grid.base, `-${ev.b}`);
      else if (ev.kind === SimEventKind.EnemyBlocked) {                  // a blocked enemy strikes the troop holding it
        const v = this.active.get(ev.a); const e = sim.findEnemy(ev.a);
        if (v) this.strike.struck(ev.a, v, sim.tick, (at) => v.rig.playOnce("attack", false, null, { solo: true, startAt: at }));
        if (v?.bs && e) this.stage.struck(v.bs, e, reduced);
      }
      else if (ev.kind === SimEventKind.BombDropped) { const v = this.active.get(ev.a); if (v && !v.rig.busy) v.rig.playOnce("attack"); }
      else if (ev.kind === SimEventKind.EnemyHealed && ev.b > 0) {
        const v = this.active.get(ev.b);
        if (v && !v.rig.busy && this.casts.cast(ev.b, ev.c, sim.tick, v.rig.clipLength("attack"))) v.rig.playOnce("attack");
      }
      else if (ev.kind === SimEventKind.EnemyBoosted || ev.kind === SimEventKind.EnemySpawned) bossAbility(ev, this.active, sim);
    }
    // a raider still held by a soldier starts its next swing ahead of the strike (enemyStrike.ts)
    const interval = sim.balance.economy.troops.enemyAttackIntervalTicks + 1;
    for (const [id, v] of this.active) {
      const e = sim.findEnemy(id);
      if (e !== null) this.strike.anticipate(id, e, v, sim.tick, interval, (at) => v.rig.playOnce("attack", false, null, { solo: true, startAt: at }));
    }
    // Health after all of this tick's hits: the bar, and the `hit` clip only for a heavy hit (≥ 8 % of max hp) so a
    // unit under a tesla or archer stream stops flinching permanently (units F5).
    for (const [id, v] of this.active) {
      if (v.doomed) continue;                          // dead, waiting for the round that killed it
      const e = sim.findEnemy(id);
      if (e === null) { this.release(v, id); this.active.delete(id); statusGate.forget(id); this.strike.forget(id); this.casts.forget(id); continue; }
      v.simHp = e.hp.toNumber();
      showHp(v, v.simHp);
      if (v.bs) this.stage.tick(v.bs, e, v.bar.hp, reduced);
    }
  }

  interpolate(alpha: number, dt: number): void {
    this.time += dt;
    UnitFx.time.value = this.time;
    this.rings.time.value = this.time;
    const reduced = this.reduced;
    this.rings.begin();
    this.marks.begin();
    for (const view of this.active.values()) {
      const hover = stepUnit(view, alpha, dt, this.time, this.stage, reduced);
      view.look?.update(dt, this.time, reduced);
      view.bar.update(dt);
      const shown = view.bs ? 1 : Math.min(1, view.age / EMERGE_SECONDS);
      if (!view.hq && !view.bs) view.body.scale.setScalar(view.scale * emergeScale(view.age));
      this.ringOf(view, hover, shown);
      drawMarks(view, hover, shown, dt, this.marks, this.fx, this.time, reduced);
    }
    for (let i = this.dying.length - 1; i >= 0; i--) {
      const v = this.dying[i];
      KillFx.onDying(v, this.fx, dt, reduced);                                    // MP-23: a worn kill effect's trail
      if (dieStep(v, dt, reduced, this.time, this.stage, this.ringOf)) { this.dying.splice(i, 1); this.park(v); }
    }
    this.showpiece.frame(dt, reduced, (v) => this.dying.includes(v));
    this.rings.end();
    this.marks.end(dt);
  }


  /**
   * Damage: a hit shows now, or — brought by a round still in flight (`key` ≥ 0) — when that round lands, the bar
   * keeping the health it had until then. Burn damage-over-time is not a hit — it shows as the burn glow, or a burning
   * unit would strobe: from its burning tower, only the damage of that tower's round (`byRound`, after its TowerFired
   * in the buffer: statusFx.ts roundOf) is a hit, never the burn tick before it, even on a tick the tower fires again.
   */
  private onDamaged(id: number, sourceId: number, amount: number, byRound: boolean, v: EnemyView, key: number, reduced: boolean): void {
    const e = this.sim.findEnemy(id);
    const hit = byRound || !(e && e.burnTicksLeft > 0 && sourceId === e.burnSourceTowerId);
    const t = sourceId > 0 ? this.sim.findTower(sourceId) : null;
    const tx = t ? t.cell.x : NaN, tz = t ? t.cell.y : NaN;   // the tower may be sold before its round lands
    if (key < 0) { if (hit) hitFeel(v, tx, tz, reduced); return; }
    const owed = Math.max(0, Math.min(amount, v.runHp));    // a killing blow owes what was left, not the overkill
    v.runHp -= owed;
    v.owed += owed;
    impactGate.wait(key, id, () => {
      const w = this.active.get(id);
      if (w !== v) return;                               // it died of something else first, or leaked: nothing to show
      v.owed = Math.max(0, v.owed - owed);
      if (hit) hitFeel(v, tx, tz, this.reduced);
      showHp(v, v.doomed ? 0 : this.sim.findEnemy(id)?.hp.toNumber() ?? 0);
    });
  }

  /**
   * A kill: the "+N" and the death, now — or, killed by a round still in flight, when it lands. Until then the body
   * walks on at its last pace along the road (enemyGhost.ts), so it does not stop dead and wait for the round.
   */
  private onKilled(id: number, points: number, v: EnemyView, key: number, reduced: boolean): void {
    const body = this.sim.enemySlots.find((x) => x.id === id);   // dead: findEnemy no longer returns it
    if (body) { v.cur.set(body.x.toNumber(), 0, body.y.toNumber()); ghostOf(body.distance.toNumber(), v.prev, v.cur, v.ghost); }
    else v.ghost.step = 0;
    const blast = !v.boss && !v.air && !reduced ? blastFrom(v, this.impacts) : null;
    const die = () => { floatAt(this.marks, v, `+${points}`, FLOAT.gold, FLOAT.killPx); this.kill(id, v, this.reduced, blast); };
    if (key < 0) { die(); return; }
    v.doomed = true;
    impactGate.wait(key, id, () => { if (this.active.get(id) === v && v.doomed) die(); });
  }

  /**
   * Unit LOD (units review F9): the level sets this to "is the camera out at gameplay zoom?". While it is, a newly
   * spawned grunt uses `<model>_lod1` when the set ships one: the same file simplified to ~3.5 k triangles with
   * half-size textures, so its nodes (`.body`, `.bomb`, `.weapon`), clips and material slots are the full model's.
   * The choice is made per spawn (the pool is keyed by model), never mid-life, so a zoom never pops a live unit.
   */
  static lodFar: () => boolean = () => false;
  /** Camera zoom at or beyond which new grunts spawn with their LOD (IsoCamera: 0.24 closest, 0.38 default, 1 farthest). */
  static readonly LodZoom = 0.34;


  private acquire(e: Enemy): EnemyView {
    const arch = this.sim.balance.enemies[e.archetype];
    const rare = rareIndexOf(this.sim, e), wyrm = isWyrmArchetype(this.sim.balance, e.archetype);
    // M40: the Wyrm is the showpiece: always its full model, never the far-zoom LOD; so is a rare (rares review
    // 2026-10-27: spawned at the default zoom, past LodZoom, it always came as its 4 k / 256 px `_lod1`)
    // Pets v2: the wild pup wears the pet's own model (render/views/wildPetView.ts draws it in its coat; this one hides)
    // (whichever pet's model is drawn for it: its own, or the stand-in's until its art ships — SkinKits.petModel)
    const petModel = arch.wildPet !== "" ? SkinKits.petModel(arch.wildPet, null) : "";
    const model = petModel && lib()?.hq(petModel) ? `hq:${petModel}` : enemyModel(arch.id, lodPick(rare, wyrm, EnemyViews.lodFar));
    const wild = arch.wildPet !== "";
    const role = enemyLook(arch, (e.isBoss && !wild) || rare > 0, hasOwnArt(arch.id));
    const key = `${model}|${role.role}${e.isAir ? "^" : ""}${rare > 0 ? `*${rare}` : ""}${wyrm ? "*w" : ""}${wild ? "*p" : ""}`;
    const list = this.pool.get(key);
    let v = list?.pop();
    const archId = arch.id;
    if (!v) {
      v = buildEnemyView(e, archId, model, role, key, (m, hq) => this.heightOf(m, hq), this.stage, rare, wyrm, wild);
      this.group.add(v.root);
    }
    resetEnemyView(v, e, arch, role);
    if (rare > 0) this.showpiece.begin(v, rareOfChapter(rare));
    else if (wyrm) this.showpiece.beginWyrm(v, wyrmDef(this.sim.balance));
    v.body.visible = true;                                 // a pooled view may have been a polymorphed raider's
    return v;
  }


  private heightOf(model: string, hq: boolean): number {
    let h = this.heights.get(model);
    if (h === undefined) {
      h = (hq ? lib()?.height(model) : undefined) || (hq ? 0.45 : 1.0);
      this.heights.set(model, h);
    }
    return h;
  }

  /** Look of an archetype for other views (death bursts, HUD), in the current palette. */
  lookOf(archetype: number, boss: boolean): EnemyLook { return enemyLook(this.sim.balance.enemies[archetype], boss); }

  /**
   * Death (units F4). A kill in the same tick as a cannon/mortar/fire impact within 0.6 cells flies on a ballistic
   * arc; a flyer spirals down; everything else plays the baked topple. All three then dissolve, and the ring and bar
   * fade. The clip's sink (−0.3, baked) is neutralised in `dieStep`, so nothing clips through the ground.
   */
  private kill(id: number, v: EnemyView, reduced: boolean, blast: { x: number; z: number } | null): void {
    this.active.delete(id);
    this.fell.set(id, v.root.position.clone());
    statusGate.forget(id);
    this.strike.forget(id);
    this.casts.forget(id);
    v.bar.dying = true;
    v.dead = 0;
    v.doomed = false; v.owed = 0;
    v.knock.x = v.knock.v = 0;
    if (v.bs) this.stage.death(v.bs);
    if (!v.hq) { this.park(v); return; }
    startDeath(v, reduced, blast, this.fx);
    KillFx.onKill(v, this.fx, reduced);                                           // MP-23: a worn kill effect's burst
    this.dying.push(v);
  }

  /**
   * Gone without a kill (leaked into the base): straight back to the pool — but the Starfall Wyrm that flew off (M40,
   * WyrmFled: no leak) soars up and out of frame first, its trail behind it.
   */
  private release(v: EnemyView, id: number): void {
    if (v.bs) this.stage.gone(v.bs);                       // a boss that walked in: its bar, dusk and bed let go too
    const run = this.sim.wyrmRun;
    if (v.wyrm && v.hq && run !== null && run.enemyId === id && run.outcome === "fled") { wyrmFlee(v); this.dying.push(v); return; }
    this.park(v);
  }

  private park(v: EnemyView): void {
    this.showpiece.end(v);
    v.root.visible = false;
    v.dead = 0;
    if (!this.pool.has(v.key)) this.pool.set(v.key, []);
    this.pool.get(v.key)!.push(v);
  }
}
